// A large dialog for real work (lists, tables, editors): wide, a fixed
// height, header and footer pinned, only the body scrolls.
//
//   <WorkspaceDialog title="Subjects of B.Tech CE" icon={BookOpen}
//       description="…" onClose={…} footer={<Button>…</Button>}>
//       …body…
//   </WorkspaceDialog>
//
// size "lg" (default) suits tables and editors; "md" suits short lists;
// "full" takes nearly the whole screen, for browsing large hierarchies.
// `back` adds a "‹ label" link above the title for nested views.
// Note: the base DialogContent sets `sm:max-w-lg`, so widths here must use
// the `sm:` prefix too or they lose on anything wider than a phone.
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronLeft } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type Props = {
    title: ReactNode;
    icon?: LucideIcon;
    description?: ReactNode;
    back?: { label: string; onClick: () => void };
    headerExtra?: ReactNode;          // right side of the header, e.g. tabs or a button
    footer?: ReactNode;
    size?: "md" | "lg" | "full";
    onClose: () => void;
    preventClose?: boolean;           // e.g. while saving
    bodyClassName?: string;
    children: ReactNode;
};

export function WorkspaceDialog({
    title, icon: Icon, description, back, headerExtra, footer, size = "lg",
    onClose, preventClose, bodyClassName, children,
}: Props) {
    return (
        <Dialog open onOpenChange={(o) => { if (!o && !preventClose) onClose(); }}>
            <DialogContent
                className={cn(
                    "flex flex-col gap-0 overflow-hidden p-0",
                    size === "full" ? "h-[95vh] w-[97vw] sm:max-w-[97vw]" : size === "lg" ? "h-[88vh] sm:max-w-6xl" : "h-[80vh] sm:max-w-3xl",
                )}
            >
                <DialogHeader className="shrink-0 border-b px-6 pt-5 pb-4 pr-14">
                    {back && (
                        <button type="button" onClick={back.onClick}
                            className="mb-1 flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                            <ChevronLeft className="h-3.5 w-3.5" /> {back.label}
                        </button>
                    )}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div className="min-w-0">
                            <DialogTitle className="flex items-center gap-2 text-lg">
                                {Icon && <Icon className="h-5 w-5 shrink-0 text-primary" />}
                                <span className="truncate">{title}</span>
                            </DialogTitle>
                            {description && <DialogDescription className="mt-1">{description}</DialogDescription>}
                        </div>
                        {headerExtra}
                    </div>
                </DialogHeader>

                <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-5", bodyClassName)}>{children}</div>

                {footer && <div className="shrink-0 border-t bg-muted/20 px-6 py-3">{footer}</div>}
            </DialogContent>
        </Dialog>
    );
}
